/**
 * API Hooks for Agents
 * 
 * React Query hooks for agent-related API calls
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getApiClient } from '@/lib/api-client';
import { useAuth, useAuthTokenReady } from './use-auth';

export interface AdminAgent {
  id: string;
  code?: string;
  name: string;
  provider: string;
  provider_country?: string;
  description?: string;
  excerpt?: string;
  moderationStatus: 'pending' | 'draft' | 'published' | 'unpublished' | 'rejected';
  visibility?: 'public' | 'private' | 'unlisted';
  tags?: string[];
  categories?: string[];
  logo?: string;
  pictures?: string[];
  videos?: Array<{
    title: string;
    url: string;
    thumbnail?: string;
    duration?: string;
    views?: string;
    channel?: string;
  }>;
  capabilities?: string[];
  deployments?: string[];
  rating?: number;
  reviewCount?: number;
  seo_title?: string;
  seo_description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AgentListParams {
  page: number;
  pageSize: number;
  query?: string;
  statusFilter?: 'all' | 'pending' | 'draft' | 'published' | 'unpublished' | 'rejected';
  sortBy?: 'createdAt' | 'updatedAt';
  sortDir?: 'asc' | 'desc';
}

export interface AgentListResponse {
  agents: AdminAgent[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface StatusCounts {
  all: number;
  pending: number;
  draft: number;
  published: number;
  unpublished: number;
  rejected: number;
}

// Query keys
export const agentKeys = {
  all: ['agents'] as const,
  lists: () => [...agentKeys.all, 'list'] as const,
  list: (params: AgentListParams) => [...agentKeys.lists(), params] as const,
  details: () => [...agentKeys.all, 'detail'] as const,
  detail: (id: string) => [...agentKeys.details(), id] as const,
  statusCounts: () => [...agentKeys.all, 'statusCounts'] as const,
};

/**
 * Hook to fetch agents list
 */
export function useAgentsList(params: AgentListParams) {
  const apiClient = getApiClient();
  const { user, loading } = useAuth();
  const tokenReady = useAuthTokenReady();
  
  const isEnabled = !loading && !!user && tokenReady;
  
  // Debug logging
  useEffect(() => {
    console.log('🔍 useAgentsList Debug:', {
      loading,
      hasUser: !!user,
      userEmail: user?.email,
      tokenReady,
      isEnabled,
      params,
    });
  }, [loading, user, tokenReady, isEnabled, params]);
  
  return useQuery<AgentListResponse>({
    queryKey: agentKeys.list(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        page: params.page.toString(),
        pageSize: params.pageSize.toString(),
      });
      
      if (params.query) {
        queryParams.append('query', params.query);
      }
      
      if (params.statusFilter && params.statusFilter !== 'all') {
        queryParams.append('statusFilter', params.statusFilter);
      }
      
      if (params.sortBy) {
        queryParams.append('sortBy', params.sortBy);
      }
      
      if (params.sortDir) {
        queryParams.append('sortDir', params.sortDir);
      }
      
      console.log('🔍 useAgentsList: Making API request...');
      const result = await apiClient.request<AgentListResponse>(`/admin/agents?${queryParams.toString()}`);
      console.log('🔍 useAgentsList: API response received:', {
        agentsCount: result?.agents?.length ?? 0,
        total: result?.total ?? 0,
      });
      return result;
    },
    enabled: isEnabled, // Only fetch when authenticated and token is ready
    retry: (failureCount, error: any) => {
      // Don't retry on 401 errors
      if (error?.status === 401) {
        console.error('🔍 useAgentsList: 401 error, not retrying');
        return false;
      }
      return failureCount < 2;
    },
  });
}

/**
 * Hook to fetch status counts
 */
export function useStatusCounts() {
  const apiClient = getApiClient();
  const { user, loading } = useAuth();
  const tokenReady = useAuthTokenReady();
  
  return useQuery<StatusCounts>({
    queryKey: agentKeys.statusCounts(),
    queryFn: async () => {
      return apiClient.request<StatusCounts>('/admin/agents/status-counts');
    },
    enabled: !loading && !!user && tokenReady, // Only fetch when authenticated and token is ready
  });
}

/**
 * Hook to fetch a single agent
 */
export function useAgent(id: string) {
  const apiClient = getApiClient();
  const { user, loading } = useAuth();
  const tokenReady = useAuthTokenReady();
  
  return useQuery<AdminAgent>({
    queryKey: agentKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.request<{ agent: AdminAgent }>(`/admin/agents/${id}`);
      return response.agent;
    },
    enabled: !loading && !!user && tokenReady && !!id, // Only fetch when authenticated, token ready, and id exists
  });
}

export interface UpdateAgentPayload {
  name?: string;
  code?: string;
  provider?: string;
  provider_country?: string;
  description?: string;
  excerpt?: string;
  moderationStatus?: 'pending' | 'draft' | 'published' | 'unpublished' | 'rejected';
  visibility?: 'public' | 'private' | 'unlisted';
  tags?: string[];
  categories?: string[];
  logo?: string;
  pictures?: string[];
  videos?: Array<{
    title: string;
    url: string;
    thumbnail?: string;
    duration?: string;
    views?: string;
    channel?: string;
  }>;
  capabilities?: string[];
  deployments?: string[];
  website_url?: string;
  docs_url?: string;
  seo_title?: string;
  seo_description?: string;
}

/**
 * Hook to update an agent
 */
export function useUpdateAgent() {
  const apiClient = getApiClient();
  const queryClient = useQueryClient();
  
  return useMutation<AdminAgent, Error, { id: string; payload: UpdateAgentPayload }>({
    mutationFn: async ({ id, payload }) => {
      const response = await apiClient.request<{ agent: AdminAgent }>(
        `/admin/agents/${id}`,
        {
          method: 'PUT',
          body: JSON.stringify(payload),
        }
      );
      return response.agent;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch agent detail
      queryClient.invalidateQueries({ queryKey: agentKeys.detail(variables.id) });
      // Invalidate agent lists to refresh counts
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agentKeys.statusCounts() });
    },
  });
}

export interface CreateAgentPayload {
  name: string;
  code?: string;
  provider: string;
  provider_country?: string;
  description?: string;
  excerpt?: string;
  moderationStatus?: 'draft' | 'pending' | 'published';
  visibility?: 'public' | 'private' | 'unlisted';
  tags?: string[];
  categories?: string[];
  logo?: string;
  pictures?: string[];
  videos?: Array<{
    title: string;
    url: string;
    thumbnail?: string;
    duration?: string;
    views?: string;
    channel?: string;
  }>;
  capabilities?: string[];
  deployments?: string[];
  website_url?: string;
  docs_url?: string;
  seo_title?: string;
  seo_description?: string;
}

/**
 * Hook to create a new agent
 */
export function useCreateAgent() {
  const apiClient = getApiClient();
  const queryClient = useQueryClient();

  return useMutation<AdminAgent, Error, CreateAgentPayload>({
    mutationFn: async (payload) => {
      const response = await apiClient.request<{ agent: AdminAgent }>(
        '/admin/agents',
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      );
      return response.agent;
    },
    onSuccess: () => {
      // Invalidate agent lists to refresh counts
      queryClient.invalidateQueries({ queryKey: agentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agentKeys.statusCounts() });
    },
  });
}

